import { SortingState } from '@tanstack/react-table';
import {
  isNotSelectAll,
  isValidOption,
} from 'components/forms/multiSelectField/MultiSelectField.constants';
import { SortDirection } from 'components/table/Table.types';
import { ExpectedQueryData } from 'components/table/hooks/useServerFilteredTable';
import { getPaginationParams } from 'components/table/utils/pagination';
import { getSortParam } from 'components/table/utils/sorting';
import { parseISO } from 'date-fns';
import {
  GetMessageLogsResponseTO,
  MessageLog,
  MessageLogApiTO,
} from 'models/automation/MessageLogs';
import { MessageLogTabFiltersSchemaType } from 'pages/automations/messageLog/filters/MessageLogTabFilters.schema';
import API from 'services/API';

type QueryReturnType = ExpectedQueryData<MessageLog>;

export const MESSAGE_LOGS_BASE_QUERY_KEY = 'message-logs';

export const MESSAGE_LOGS_SORTING = {
  executionTime: 'SORT_BY_EXECUTION_TIME',
  messageName: 'SORT_BY_MESSAGE_NAME',
};

export const fetchAllMessageLogs = async (queryParams: {
  _limit?: number;
  _offset?: number | undefined;
  from?: Date;
  messageName?: string;
  propertyUid?: string[];
  sentVia?: string[];
  sort?: string;
  sortDirection?: SortDirection;
  status?: string[];
  to?: Date;
}) => {
  const stringifyArrayParam = (param: keyof typeof queryParams) => {
    const field = queryParams[param];

    return Array.isArray(field) && field.length
      ? { [param]: field.join(',') }
      : {};
  };

  const response = await API.get<GetMessageLogsResponseTO>(
    '/api/internal/trigger-logs',
    {
      params: {
        ...queryParams,
        ...stringifyArrayParam('propertyUid'),
        ...stringifyArrayParam('sentVia'),
        ...stringifyArrayParam('status'),
      },
    },
  );

  return response.data;
};

const parseMessageLog = (data: MessageLogApiTO): MessageLog => {
  return {
    ...data,
    createdAt: data.createdAt ? parseISO(data.createdAt) : null,
    executionTime: data.executionTime ? parseISO(data.executionTime) : null,
    lastRetryTime: data.lastRetryTime ? parseISO(data.lastRetryTime) : null,
    updatedAt: data.updatedAt ? parseISO(data.updatedAt) : null,
  };
};

export const messageLogsQuery = ({
  filters,
  pageIndex,
  pageSize,
  searchTerm,
  sorting,
}: {
  filters: Partial<MessageLogTabFiltersSchemaType>;
  pageIndex: number;
  pageSize: number;
  searchTerm?: string;
  sorting: SortingState;
}) => ({
  queryKey: [
    MESSAGE_LOGS_BASE_QUERY_KEY,
    'list',
    { filters, pageIndex, searchTerm, sorting },
  ],
  queryFn: async (): Promise<QueryReturnType> => {
    const sort = getSortParam(sorting, MESSAGE_LOGS_SORTING);

    const data = await fetchAllMessageLogs({
      from: filters.from ?? undefined,
      propertyUid: filters.propertyUid
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      sentVia: filters.sentVia
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      status: filters.status
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      to: filters.to ?? undefined,
      ...(searchTerm ? { messageName: searchTerm } : {}),
      ...getPaginationParams({ pageIndex, pageSize }),
      ...(sort ? { sort: sort.sort, sortDirection: sort.direction } : {}),
    });

    const { triggerLogs, _metadata } = data;

    return {
      data: triggerLogs.map(parseMessageLog),
      _metadata,
    };
  },
  enabled: true,
});
